import { loadGoogleMaps } from "./googleMapsLoader";
import {
  getLatLngFromBlockNumber,
  integerToBlock,
} from "./pixel";

export const DEFAULT_FLY_ALTITUDE = 2175.13;
export const DEFAULT_FLY_RANGE = 5000;
export const DEFAULT_FLY_DURATION_MS = 1000;

export interface LatLng {
  lat: number;
  lng: number;
}

export type ParsedWorldSearch =
  | { kind: "coords"; lat: number; lng: number }
  | { kind: "pixel"; pixel: number; lat: number; lng: number }
  | { kind: "geocode"; address: string };

/** Persist camera focus like legacy `flyToCoordinates`. */
export function rememberMapPosition(lat: number, lng: number) {
  localStorage.setItem("lat", String(lat));
  localStorage.setItem("lng", String(lng));
}

export function flyMapTo(
  map: google.maps.maps3d.Map3DElement,
  lat: number,
  lng: number,
  opts?: { altitude?: number; range?: number; durationMillis?: number },
) {
  const altitude = opts?.altitude ?? DEFAULT_FLY_ALTITUDE;
  const range = opts?.range ?? DEFAULT_FLY_RANGE;
  map.flyCameraTo?.({
    endCamera: {
      center: { lat, lng, altitude },
      range,
      tilt: 20,
      heading: 0,
    },
    durationMillis: opts?.durationMillis ?? DEFAULT_FLY_DURATION_MS,
  });
  rememberMapPosition(lat, lng);
}

/** `48.203, 16.396` / `48.203 16.396` (also accepts `,` as decimal separator). */
export function parseLatLngPair(query: string): LatLng | null {
  const t = query.trim();
  if (!t) return null;

  const parts = t.split(/[,;]+|\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length !== 2) return null;

  const lat = Number.parseFloat(parts[0].replace(",", "."));
  const lng = Number.parseFloat(parts[1].replace(",", "."));
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    Math.abs(lat) > 90 ||
    Math.abs(lng) > 180
  ) {
    return null;
  }
  return { lat, lng };
}

/** Whole-number pixel id (legacy grid integer). */
export function parsePixelId(query: string): number | null {
  const t = query.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isSafeInteger(n) || n <= 0) return null;
  return n;
}

export function latLngFromPixelId(pixel: number): LatLng {
  const [x, y] = integerToBlock(pixel);
  const [lat, lng] = getLatLngFromBlockNumber(x, y);
  return { lat, lng };
}

/**
 * Classify header search: coordinates, pixel id, or free-text geocode query.
 * Pixel is only used when the string is digits-only (so `48,16` stays coords).
 */
export function parseWorldSearchQuery(query: string): ParsedWorldSearch | null {
  const t = query.trim();
  if (!t) return null;

  const coords = parseLatLngPair(t);
  if (coords) return { kind: "coords", ...coords };

  const pixel = parsePixelId(t);
  if (pixel != null) {
    const { lat, lng } = latLngFromPixelId(pixel);
    return { kind: "pixel", pixel, lat, lng };
  }

  return { kind: "geocode", address: t };
}

export interface GeocodePlace {
  label: string;
  lat: number;
  lng: number;
}

export async function geocodePlaces(address: string): Promise<GeocodePlace[]> {
  await loadGoogleMaps();
  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.length) {
        resolve(
          results.map((r) => ({
            label: r.formatted_address,
            lat: r.geometry.location.lat(),
            lng: r.geometry.location.lng(),
          })),
        );
        return;
      }
      if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
        resolve([]);
        return;
      }
      reject(new Error(status));
    });
  });
}
